import React, { useState, useEffect } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import AppSidebar from '../../components/AppSidebar';
import AppHeader from '../../components/AppHeader';
import PageHeader from '../../components/PageHeader';
import LeadsService from '../../services/LeadsService';
import { FaRegEdit } from "react-icons/fa";
import { ToastContainer, toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import LogsTable from '../../components/LogsTable';
import CallLogService from '../../services/CallLogService';
import UsersService from '../../services/UsersService';
import CallLog from '../../modals/CallLog';
import Assign from '../../modals/Assign';
import ChangeStatusModal from '../../modals/ChangeStatus';
import RecentActivity from '../../components/RecentActivity';
import ViewCard from '../../components/ViewCard';
import { leadStatusOptions } from '../../utils/OptionUtils';
import ConfirmModal from '../../modals/ConfirmModal';

function ViewLead() {
    const { id } = useParams();
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [statusData, setStatusData] = useState('');
    const { register, handleSubmit, formState: { errors }, reset } = useForm();
    const { handleSubmit: handleAssigneeSubmit, control: assigneeControl } = useForm();
    const { control: statusControl, handleSubmit: handleStatusSubmit } = useForm();
    const [callLogData, setCallLogData] = useState({});
    const [AssigneeList, setAssigneeList] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const handleCloseAssignModal = () => setShowAssignModal(false);
    const handleShowAssignModal = () => setShowAssignModal(true);
    const [showCallLogModal, setShowCallLogModal] = useState(false);
    const handleCloseCallLogModal = () => setShowCallLogModal(false);
    const handleShowCallLogModal = () => { setShowCallLogModal(true); }
    const [showStatusModal, setShowStatusModal] = useState(false);
    const handleCloseStatusModal = () => setShowStatusModal(false);
    const handleShowStatusModal = () => setShowStatusModal(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const fields = [
        { label: 'Name', key: 'name' },
        { label: 'Email', key: 'email' },
        { label: 'Phone', key: 'phone' },
        { label: 'Company', key: 'company' },
        { label: 'Address', key: 'address' },
        { label: 'City', key: 'city' },
        { label: 'State', key: 'state' },
        { label: 'Country', key: 'country' },
        { label: 'Zip Code', key: 'zipcode' },
        { label: 'No. of users/Licence', key: 'value' },
        { label: 'Tags', key: 'tags' },
        { label: 'Source', key: 'source' },
        { label: 'Designation', key: 'designation' },
        { label: 'Department', key: 'department' },
        { label: 'No. of employees', key: 'employee_count' },
        { label: 'Website', key: 'website' },
        { label: 'Assigned To', key: 'assignedTo', render: (assignedTo) => assignedTo?.name || 'N/A' },
        { label: 'Product', key: 'product', render: (product) => product?.name || 'N/A' },
        {
            label: 'Status',
            key: 'status',
            render: (status) => {
                const badgeClass = {
                    'call-done': 'bg-success',
                    'follow-up': 'bg-warning',
                    'in-progress': 'bg-info',
                    'invalid': 'bg-danger',
                    'loss': 'bg-secondary',
                    'mail-done': 'bg-success',
                    'no-action': 'bg-dark',
                    'on-hold': 'bg-warning',
                    'price-awaited': 'bg-primary',
                    'quotation-shared': 'bg-info',
                    'won': 'bg-success',
                }[status] || 'bg-secondary';

                const statusText = {
                    'call-done': 'Call Done',
                    'follow-up': 'Follow Up',
                    'in-progress': 'In Progress',
                    'invalid': 'Invalid',
                    'loss': 'Loss',
                    'mail-done': 'Mail Done',
                    'no-action': 'No Action',
                    'on-hold': 'On Hold',
                    'price-awaited': 'Price Awaited',
                    'quotation-shared': 'Quotation Shared',
                    'won': 'Won',
                }[status] || 'Unverified';

                return <span className={`badge ${badgeClass}`}>{statusText}</span>;
            }
        }

    ];

    const today = new Date().toISOString().split('T')[0];
    const [allCallLogs, setAllCallLogs] = useState([]);
    const type = "lead";

    useEffect(() => {
        const fetchLead = async () => {
            try {
                const response = await LeadsService.get(id);
                if (response?.status) {
                    setLead(response?.data?.data);
                } else {
                    setError(response?.data?.message);
                }
            } catch (error) {
                setError('Something went wrong');
            } finally {
                setLoading(false);
            }
        };

        const fetchAssignees = async () => {
            try {
                const response = await UsersService.list();
                const assignees = response.data.data;

                const formattedAssigneeList = assignees.map((assignee) => ({
                    value: assignee.id,
                    label: assignee.name + '-' + assignee.email,
                }));

                setAssigneeList(formattedAssigneeList);
            } catch (err) {
                toast.error('Failed to load assignees');
            }
        };

        const fetchCallLogs = async () => {
            try {
                setLoading(true);
                const response = await CallLogService.getLogByType(type, id);
                if (response.status === 200 && response.data.success) {
                    setAllCallLogs(response.data.data || []);
                } else {
                    console.error('Error fetching data:', response);
                }
            } catch (error) {
                console.error('Error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCallLogs();
        fetchLead();
        fetchAssignees();

    }, [id]);

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    const onSubmit = async (data) => {

        try {
            const requestBody = {
                data: {
                    type: type,
                    type_id: parseInt(id) || 0,
                    status: data.status || 'N/A',
                    feedback_type: data.feedback_type || "negative",
                    response: data.response || "not_interested",
                    remarks: data.remarks || "NA",
                    rescheduled_at: data.date || null,
                }
            };

            const response = await CallLogService.createLog(requestBody);
            if (response.status === 201) {
                setAllCallLogs(prevLogs => [...prevLogs, response.data.data]);
                handleCloseCallLogModal();
                toast.success('Call log saved successfully!');
            } else {
                toast.error('Failed to save call log');
            }
        } catch (error) {
            toast.error('Something went wrong while saving the data');
        }
    };

    const onSubmitAssignee = async (data) => {
        const assigneeId = data.assignee.map((assignee) => assignee.value);
        const requestBody = {
            data: {
                assigned_to: assigneeId
            }
        };
        try {
            const response = await LeadsService.leadAssignToUser(id, requestBody);
            if (response.status === 200) {
                const assignResponse = await LeadsService.get(id);
                if (assignResponse.status) {
                    setLead(assignResponse.data.data);
                } else {
                    toast.error(response?.data?.message || 'Failed to assign lead');
                }
                toast.success(response.data.message || 'Lead assigned to user successfully');
                handleCloseAssignModal();
            } else {
                toast.error(response?.data?.message || 'Failed to assign lead');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || 'Something went wrong');
        }
    };

    const onSubmitStatus = async (data) => {
        try {
            const { status } = data;
            const requestBody = {
                data: {
                    status: status ? status.value : null,
                },
            };
            const response = await LeadsService.changeLeadStatus(id, requestBody);
            if (response.status === 200) {
                setLead((prevLead) => ({ ...prevLead, status: status.value }));
                toast.success("Lead status updated successfully");
                handleCloseStatusModal();
            }
            else {
                toast.error(response.message || "An error occurred.");
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred.");
        }
    };

    const handleCallLog = (e) => {
        const value = e.target.value;
        setSelectedStatus(value);
        setCallLogData(prevState => ({ ...prevState, status: value }));
        if (value === 'connected' || value === 'disconnected') {
            setStatusData(
                <div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-group">
                            <label htmlFor="rescheduleDate" className="form-label">Reschedule Date & Time</label>
                            <input
                                type="date"
                                className="form-control mb-2"
                                id="rescheduleDate"
                                {...register('date', { required: 'Date is required' })}
                                onChange={(e) => setCallLogData({ ...callLogData, date: e.target.value })}
                            />
                            {errors.date && <p className="text-danger">{errors.date.message}</p>}
                        </div>
                        <div className="form-group mt-3">
                            <textarea
                                className="form-control"
                                id="callRemark"
                                rows="2"
                                placeholder="Call Remark"
                                {...register('remarks', { required: 'Remark is required' })}
                                onChange={(e) => setCallLogData({ ...callLogData, remarks: e.target.value })}
                            ></textarea>
                            {errors.remarks && <p className="text-danger">{errors.remarks.message}</p>}
                        </div>
                        {value === 'connected' && (
                            <div className="form-group mt-3">
                                <select
                                    className="form-select"
                                    {...register('feedback_type', { required: 'Feedback type is required' })}
                                    onChange={(e) => setCallLogData({ ...callLogData, feedback_type: e.target.value })}
                                >
                                    <option value="">Select Feedback Type</option>
                                    <option value="positive">Positive</option>
                                    <option value="negative">Negative</option>
                                </select>
                                {errors.feedback_type && <p className="text-danger">{errors.feedback_type.message}</p>}
                            </div>
                        )}
                        <div className="form-group mt-3">
                            <select
                                className="form-select"
                                {...register('response', { required: 'response type is required' })}
                                onChange={(e) => setCallLogData({ ...callLogData, response: e.target.value })}
                            >
                                <option value="">Select Response Type</option>
                                <option value="interested">Interested</option>
                                <option value="not_interested">Not Interested</option>
                            </select>
                            {errors.feedback_type && <p className="text-danger">{errors.feedback_type.message}</p>}
                        </div>
                    </form>
                </div>
            );
        } else {
            setStatusData(null);
        }
    };
    const handleShowConfirmModal = () => {
        setShowConfirmModal(true);
    };
    const handleClose = () => {
        setShowConfirmModal(false);
    }
    const handleConfirm = async (id) => {
        const data = { status: 'won' };
        try {
            const response = await LeadsService.changeLeadStatus(id, { data });
            if (response.status === 200) {
                toast.success(response?.data?.message || "Lead converted to customer successfully");
                handleClose();
            } else {
                toast.error(response?.data?.message || "An error occurred.");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "An error occurred.");
            console.error('Error:', error);
        }
    };
    return (
        <>
            <AppSidebar />
            <div className="main-content">
                <AppHeader />
                <div className="container-fluid">
                    <div className="row">
                        <PageHeader
                            title='Customer Lead'
                            button_label="Log a Call"
                            button_label1="Assign"
                            button_label2="Convert to Customer"
                            buttonAttributes={{ onClick: handleShowCallLogModal }}
                            buttonAttributes1={{ onClick: handleShowAssignModal }}
                            buttonAttributes2={{ onClick: handleShowConfirmModal }}
                            className="btn btn-sm btn-primary me-2"
                            className1="btn btn-sm btn-info"
                            className2="btn btn-sm btn-success ms-2"
                        />
                        <div className="container card p-3">
                            <div>
                                <NavLink className='float-end pb-4' to={`/leads/${id}/edit`}>
                                    <FaRegEdit size={20} />
                                </NavLink>
                            </div>
                            <div className="row">
                                <ViewCard data={lead} handleShowStatusModal={handleShowStatusModal} fields={fields} />
                                <div className="col-12 col-md-12 col-lg-4 mb-4">
                                    <RecentActivity type={type} />
                                </div>
                            </div>
                            <LogsTable allCallLogs={allCallLogs} />
                        </div>
                    </div>
                </div>

                {/* Call Logs Modal */}

                <CallLog
                    show={showCallLogModal}
                    handleClose={handleCloseCallLogModal}
                    handleSubmit={handleSubmit}
                    onSubmit={onSubmit}
                    register={register}
                    errors={errors}
                    selectedStatus={selectedStatus}
                    handleCallLog={handleCallLog}
                    statusData={statusData}
                    reset={reset}
                    today={today}
                />

                {/* Assign Task Modal */}
                <Assign
                    show={showAssignModal}
                    handleClose={handleCloseAssignModal}
                    handleSubmit={handleAssigneeSubmit}
                    control={assigneeControl}
                    AssigneeList={AssigneeList}
                    onSubmit={onSubmitAssignee}
                    errors={errors}
                />

                {/* Change Status Modal */}
                <ChangeStatusModal
                    show={showStatusModal}
                    handleClose={handleCloseStatusModal}
                    handleSubmit={handleStatusSubmit}
                    control={statusControl}
                    status={leadStatusOptions}
                    onSubmit={onSubmitStatus}
                    errors={errors}
                />
                {/* Confirm Modal */}
                {showConfirmModal && (
                    <ConfirmModal
                        show={showConfirmModal}
                        onCancel={handleClose}
                        onConfirm={handleConfirm}
                        id={id}
                        heading="Convert Lead to Customer"
                        content="Are you sure you want to convert this lead to customer?"
                        type="lead"
                    />
                )}
                <ToastContainer containerId={'main'} />
            </div>
        </>
    );
}

export default ViewLead;
